import { z } from "zod";

const cardSchema = z
	.object({
		brandName: z.string(),
		oneLiner: z.string().default(""),
		siteUrl: z.string().default(""),
		offers: z.array(z.string()).default([]),
		audience: z.string().nullable().default(null),
		tone: z.string().default(""),
		notablePages: z
			.array(z.object({ label: z.string(), url: z.string() }))
			.default([]),
		profiles: z
			.array(z.object({ network: z.string(), url: z.string() }))
			.default([]),
		openQuestions: z.array(z.string()).default([]),
	})
	.passthrough();

const packRowSchema = z.object({
	packId: z.string(),
	siteUrl: z.string(),
	status: z.string(),
	phase: z.string(),
	progress: z.number(),
	lastError: z.string().nullable(),
	confirmedAt: z.string().nullable(),
	card: cardSchema.nullable().catch(null),
});

const statusSchema = z.object({
	ok: z.literal(true),
	complete: z.boolean(),
	deskReady: z.boolean().optional().default(false),
	workspaceName: z.string().optional(),
	packs: z.array(packRowSchema),
	latest: packRowSchema.nullable(),
});

export type OnboardingPackRow = z.infer<typeof packRowSchema>;

export async function fetchOnboardingStatus(packId?: string): Promise<
	| {
			ok: true;
			complete: boolean;
			deskReady: boolean;
			workspaceName?: string;
			packs: OnboardingPackRow[];
			latest: OnboardingPackRow | null;
	  }
	| { ok: false; error: string }
> {
	try {
		const qs = packId ? `?packId=${encodeURIComponent(packId)}` : "";
		const response = await fetch(`/api/onboarding/status${qs}`, {
			credentials: "include",
		});
		if (!response.ok) {
			return { ok: false, error: `http_${response.status}` };
		}
		const json: unknown = await response.json();
		const parsed = statusSchema.safeParse(json);
		if (!parsed.success) return { ok: false, error: "invalid_response" };
		return parsed.data;
	} catch {
		return { ok: false, error: "network_error" };
	}
}

export async function fetchCompanyPack(
	packId: string,
): Promise<
	| { ok: true; pack: OnboardingPackRow }
	| { ok: false; error: string }
> {
	try {
		const response = await fetch(
			`/api/onboarding/${encodeURIComponent(packId)}`,
			{ credentials: "include" },
		);
		if (!response.ok) {
			return { ok: false, error: `http_${response.status}` };
		}
		const json: unknown = await response.json();
		const parsed = z
			.object({ ok: z.literal(true), pack: packRowSchema })
			.safeParse(json);
		if (!parsed.success) return { ok: false, error: "invalid_response" };
		return { ok: true, pack: parsed.data.pack };
	} catch {
		return { ok: false, error: "network_error" };
	}
}

export async function confirmOnboardingPack(packId: string): Promise<
	| {
			ok: true;
			brandName: string;
			siteUrl: string;
			nextAsk: string;
			message: string;
	  }
	| { ok: false; error: string; message?: string }
> {
	try {
		const response = await fetch(
			`/api/onboarding/${encodeURIComponent(packId)}/confirm`,
			{ method: "POST", credentials: "include" },
		);
		const json: unknown = await response.json().catch(() => null);
		if (!response.ok) {
			const err = z
				.object({
					ok: z.literal(false).optional(),
					error: z.string().optional(),
					message: z.string().optional(),
				})
				.safeParse(json);
			return {
				ok: false,
				error: err.success ? (err.data.error ?? `http_${response.status}`) : `http_${response.status}`,
				message: err.success ? err.data.message : undefined,
			};
		}
		const parsed = z
			.object({
				ok: z.literal(true),
				brandName: z.string(),
				siteUrl: z.string(),
				nextAsk: z.string(),
				message: z.string(),
			})
			.safeParse(json);
		if (!parsed.success) return { ok: false, error: "invalid_response" };
		return parsed.data;
	} catch {
		return { ok: false, error: "network_error" };
	}
}

export async function patchWorkspaceName(
	name: string,
): Promise<{ ok: true; name: string } | { ok: false; error: string }> {
	try {
		const response = await fetch("/api/onboarding/workspace", {
			method: "PATCH",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ name }),
		});
		const json: unknown = await response.json().catch(() => null);
		if (!response.ok) {
			return { ok: false, error: `http_${response.status}` };
		}
		const parsed = z
			.object({ ok: z.literal(true), name: z.string() })
			.safeParse(json);
		if (!parsed.success) return { ok: false, error: "invalid_response" };
		return parsed.data;
	} catch {
		return { ok: false, error: "network_error" };
	}
}

export function hrefFromSiteUrl(raw: string): string | null {
	const trimmed = raw.trim();
	if (!trimmed) return null;
	const withScheme = /^https?:\/\//i.test(trimmed)
		? trimmed
		: `https://${trimmed}`;
	try {
		const url = new URL(withScheme);
		if (url.protocol !== "http:" && url.protocol !== "https:") return null;
		return url.toString();
	} catch {
		return null;
	}
}

export async function startNewSiteBuild(url: string): Promise<
	| {
			ok: true;
			siteId: string;
			name: string;
			sourceUrl: string;
			progress: number;
			status: string;
	  }
	| { ok: false; error: string; message: string }
> {
	const href = hrefFromSiteUrl(url);
	if (!href) {
		return { ok: false, error: "invalid_input", message: "Need the current website to learn from." };
	}
	const ac = new AbortController();
	const timer = window.setTimeout(() => ac.abort(), 12_000);
	try {
		const response = await fetch("/api/sites/redesign", {
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ url: href }),
			signal: ac.signal,
		});
		const json: unknown = await response.json().catch(() => null);
		if (response.status === 402) {
			return {
				ok: false,
				error: "payment_required",
				message: "Finish checkout to start the new site.",
			};
		}
		const parsed = z
			.discriminatedUnion("ok", [
				z.object({
					ok: z.literal(true),
					siteId: z.string(),
					name: z.string(),
					sourceUrl: z.string(),
					progress: z.number(),
					status: z.string(),
				}),
				z.object({
					ok: z.literal(false),
					error: z.string(),
					message: z.string(),
				}),
			])
			.safeParse(json);
		if (!parsed.success) {
			return { ok: false, error: "invalid_response", message: "Couldn’t start the copy." };
		}
		return parsed.data;
	} catch {
		return {
			ok: false,
			error: "network_error",
			message: "Couldn’t start the copy just now. Try again.",
		};
	} finally {
		window.clearTimeout(timer);
	}
}
